import { defineStore } from 'pinia';
import { useStoreSsPlayer } from '../softswiss/player';
import { useStoreCurrencyMenu } from '../strapi/currencyMenu';
import { softswiss } from '../transport/softswiss';
import type { ICurrencyPair, ICurrencyRates } from '~/types/currency';

export const useStoreConverterCurrency = defineStore('converterCurrency', {
  state: () => ({
    price: 0,
    cryptoInFiatCurrency: 'USD',
    ssResponseData: {} as Record<string, Record<string, { total_exchange_rate: number }>>,
    cryptoRates: {
      BTC: 0,
      BCH: 0,
      ETH: 0,
      XRP: 0,
      LTC: 0,
      DOG: 0,
      TRX: 0,
      ADA: 0,
      BNB: 0,
      USDT: 1,
    } as ICurrencyRates,
    fiatRates: {
      EUR: 0,
      AUD: 0,
      CAD: 0,
      NZD: 0,
      PLN: 0,
      JPY: 0,
      BRL: 0,
      NOK: 0,
    } as ICurrencyRates,
    intervalMs: 1000 * 60 * 60, // 1 hour
    lastUpdate: 0, // timestamp
  }),
  getters: {
    currencyOptions() {
      const storeCurrencyMenu = useStoreCurrencyMenu();

      const currency = storeCurrencyMenu.activeCurrencies.map((c) => ({
        currencyCode: c.currencyCode,
        currencyIcon: c.currencyIcon,
      }));

      return currency;
    },
    rates(state): ICurrencyRates {
      return { ...state.fiatRates, ...state.cryptoRates };
    },
    cryptoCodes(state): string[] {
      return Object.keys(state.cryptoRates);
    },
    fiatCodes(state): string[] {
      return Object.keys(state.fiatRates);
    },
    cryptoInFiatCurrencyWithIcon(state) {
      const storeCurrencyMenu = useStoreCurrencyMenu();
      return {
        currencyCode: state.cryptoInFiatCurrency,
        currencyIcon: storeCurrencyMenu.currencies.find(
          (el) => el.currencyCode === state.cryptoInFiatCurrency
        )?.currencyIcon.url,
      };
    },
  },
  actions: {
    async checkRates() {
      if (!import.meta.browser || !window || this.lastUpdate + this.intervalMs > Date.now()) {
        return;
      }

      const data = await softswiss().get({ path: '/api/info/crypto_exchange_rates' });
      this.ssResponseData = data;

      this.cryptoCodes.forEach((code) => {
        this.cryptoRates[code] = data?.USD[code].total_exchange_rate;
      });

      this.fiatCodes.forEach((code) => {
        this.fiatRates[code] = 1 / data?.[code]?.USDT.total_exchange_rate;
      });

      this.lastUpdate = Date.now();

      setTimeout(this.checkRates, this.intervalMs);
    },
    getPrice(currency: ICurrencyPair, isOnlyReturnPrice?: boolean) {
      try {
        if (!import.meta.browser || !window?.location?.host) {
          return false;
        }

        const storeCurrencyMenu = useStoreCurrencyMenu();

        if (
          storeCurrencyMenu.isFiatCurrency(currency.from) &&
          storeCurrencyMenu.isCryptoCurency(currency.to)
        ) {
          const price = 1 / this.ssResponseData[currency.from][currency.to].total_exchange_rate;
          if (isOnlyReturnPrice) {
            return price;
          }
          this.price = price;
        } else if (
          storeCurrencyMenu.isCryptoCurency(currency.from) &&
          storeCurrencyMenu.isFiatCurrency(currency.to)
        ) {
          const price = this.ssResponseData[currency.to][currency.from].total_exchange_rate;
          if (isOnlyReturnPrice) {
            return price;
          }
          this.price = price;
        } else if (
          storeCurrencyMenu.isFiatCurrency(currency.from) &&
          storeCurrencyMenu.isFiatCurrency(currency.to)
        ) {
          const currencyFromInUSD = this.ssResponseData[currency.from].USDT.total_exchange_rate;
          const currencyToInUSD = this.ssResponseData[currency.to].USDT.total_exchange_rate;
          const price = currencyToInUSD / currencyFromInUSD;
          if (isOnlyReturnPrice) {
            return price;
          }
          this.price = price;
        } else if (
          storeCurrencyMenu.isCryptoCurency(currency.from) &&
          storeCurrencyMenu.isCryptoCurency(currency.to)
        ) {
          const currencyFromInUSD = this.ssResponseData.USD[currency.from].total_exchange_rate;
          const currencyToInUSD = this.ssResponseData.USD[currency.to].total_exchange_rate;
          const price = currencyFromInUSD / currencyToInUSD;
          if (isOnlyReturnPrice) {
            return price;
          }
          this.price = price;
        }
      } catch (e) {
        console.error(e);
      }
    },
    setCryptoInFiatCurrency(code: string) {
      this.cryptoInFiatCurrency = code;
      if (process.client && window.localStorage) {
        const userId = useStoreSsPlayer().playerId || '';
        const existingCryptoInFiatCurrency = window.localStorage.getItem('cryptoInFiatCurrency');
        window.localStorage.setItem(
          'cryptoInFiatCurrency',
          existingCryptoInFiatCurrency
            ? JSON.stringify({ ...JSON.parse(existingCryptoInFiatCurrency), [userId]: code })
            : JSON.stringify({ [userId]: code })
        );
      }
    },
    setInitialCryptoInFiatCurrency() {
      if (process.client && window.localStorage) {
        const existingCryptoInFiatCurrency = window.localStorage.getItem('cryptoInFiatCurrency');

        if (existingCryptoInFiatCurrency) {
          const cryptoInFiatCurrency = JSON.parse(existingCryptoInFiatCurrency);
          const userId = useStoreSsPlayer().playerId || '';
          this.cryptoInFiatCurrency = cryptoInFiatCurrency[userId] || 'USD';
        }
      }
    },
  },
});
